<!--
  ApprovalSummaryCards.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Four-card header above the Approval Workspace queue. Each card
  surfaces a top-level count drawn from the Pinia store's ``counts``
  computed (see ``filters.js``). Clicking a card applies the matching
  filter, so the cards double as one-tap navigation into the queue
  bucket that produced the number.

  Copy choices (matter-of-fact, production-grade):
    - "Awaiting client"      -> client side hasn't acted yet
    - "Needs builder"        -> rejected + changes_requested
    - "Stale"                -> reminded / escalated / expired bucket
    - "To send"              -> uploaded artworks not yet routed

  The cards inherit ``loading`` from the store and render zeros while
  the queue is fetching. They never show negative or NaN values.

  Emits:
    - update:filter(key)    Filter chip key the parent should switch to.

  Tests cover render shape, click-through, and the per-card filter key
  binding. Visual styling uses the existing tf-* design tokens.
-->
<template>
    <div class="aw-summary" data-testid="aw-summary-cards">
        <button
            v-for="card in cards"
            :key="card.key"
            type="button"
            class="aw-summary__card"
            :class="{ 'aw-summary__card--alert': card.alert }"
            :data-testid="`aw-summary-card-${card.key}`"
            @click="$emit('update:filter', card.filterKey)"
        >
            <span class="aw-summary__count">{{ card.count }}</span>
            <span class="aw-summary__label">{{ card.label }}</span>
        </button>
    </div>
</template>

<script>
export default {
    name: 'ApprovalSummaryCards',
    props: {
        counts: {
            // Shape: { awaiting_approval, needs_builder, stale_total,
            //          to_send } from filters.computeCounts(...).
            type: Object,
            required: true,
        },
    },
    emits: ['update:filter'],
    computed: {
        cards() {
            const c = this.counts || {}
            return [
                {
                    key: 'awaiting',
                    label: 'Awaiting client',
                    count: c.awaiting_approval || 0,
                    filterKey: 'awaiting_approval',
                    alert: false,
                },
                {
                    key: 'needs_builder',
                    label: 'Needs builder',
                    count: c.needs_builder || 0,
                    filterKey: 'changes_requested',
                    alert: (c.needs_builder || 0) > 0,
                },
                {
                    key: 'stale',
                    label: 'Stale',
                    count: c.stale_total || 0,
                    filterKey: 'needs_attention',
                    alert: (c.stale_total || 0) > 0,
                },
                {
                    key: 'to_send',
                    label: 'To send',
                    count: c.to_send || 0,
                    filterKey: 'to_send',
                    alert: false,
                },
            ]
        },
    },
}
</script>

<style scoped>
.aw-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}
.aw-summary__card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 16px;
    background: var(--tf-bg, #ffffff);
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-sm, 6px);
    cursor: pointer;
    text-align: left;
    transition: border-color 120ms ease, box-shadow 120ms ease;
}
.aw-summary__card:hover {
    border-color: var(--tf-primary, #1976d2);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.aw-summary__card--alert {
    border-color: #c97900;
    background: #fff8eb;
}
.aw-summary__count {
    font-size: 26px;
    font-weight: 600;
    color: var(--tf-fg-1, #1a202c);
    line-height: 1.1;
}
.aw-summary__label {
    font-size: 12px;
    color: var(--tf-fg-4, #5a6a82);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
</style>
